<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier, vue/max-attributes-per-line, vue/html-closing-bracket-newline */
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TalkviaStoreAPI from 'dashboard/api/talkviaStore';

const copy = {
  title: 'Marketplace Talkvia',
  subtitle:
    'Compre créditos e adicionais e atualize a recorrência da assinatura pela Asaas.',
  loading: 'Carregando marketplace...',
  actions: {
    add: 'Adicionar',
    remove: 'Remover',
    confirm: 'Confirmar alteração',
    cancel: 'Cancelar',
    reload: 'Atualizar',
  },
  sections: {
    catalog: 'Adicionais disponíveis',
    catalogDescription:
      'Escolha os adicionais que entram na recorrência mensal.',
    cart: 'Resumo da compra',
    billing: 'Assinatura e bloqueios',
    guidance: 'Como funciona',
  },
  fields: {
    currentSubscription: 'Assinatura atual',
    openBills: 'Boletos em aberto',
    monthlyIncrease: 'Acréscimo mensal',
    newEstimatedValue: 'Novo valor estimado',
    input: 'Entrada',
    output: 'Saída',
    item: 'Item',
    quantity: 'Quantidade',
    total: 'Total',
  },
  billing: {
    notReady: 'Cobrança não vinculada',
  },
  empty: {
    cart: 'Nenhum adicional selecionado.',
    openBills: 'Nenhum boleto em aberto.',
  },
  messages: {
    blocked:
      'Existe cobrança pendente ou vencida. Regularize antes de comprar adicionais.',
    pendingPurchase: 'Existe uma compra aguardando confirmação de pagamento.',
    billingNotReady:
      'Vincule a assinatura Asaas antes de confirmar compras no marketplace.',
    success:
      'Alteração registrada. Os adicionais serão liberados após confirmação do pagamento.',
    failed: 'Não foi possível concluir a compra.',
    loadFailed: 'Não foi possível carregar o marketplace.',
  },
  labels: {
    brand: 'Talkvia',
    dash: '-',
    plus: '+',
    recommended: 'Recomendado para começar',
    recurring: 'recorrente',
    byMonth: '/mês',
    selected: 'Selecionado',
    times: 'x',
    colon: ':',
    periodMark: '.',
    included: 'Incluso',
    notIncluded: 'Não incluso',
  },
};

const storePayload = ref({});
const quantities = reactive({});
const confirmDialogRef = ref(null);
const creditExampleDialogRef = ref(null);
const selectedCreditExample = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const buyingCreditProductId = ref(null);
const creditUnits = reactive({});

const catalog = computed(() => storePayload.value.catalog || {});
const subscription = computed(() => storePayload.value.subscription || {});
const openBankSlips = computed(() => storePayload.value.open_bank_slips || []);
const pendingPurchases = computed(
  () => storePayload.value.pending_purchases || []
);
const guidance = computed(() => storePayload.value.guidance || []);
const creditProducts = computed(() => storePayload.value.credit_products || []);
const hasOpenBankSlips = computed(() => openBankSlips.value.length > 0);
const hasPendingPurchase = computed(() => pendingPurchases.value.length > 0);
const billingWarning = computed(() => storePayload.value.billing_warning || '');
const referralDiscount = computed(() =>
  Number(storePayload.value.referral_discount_percentage || 0)
);
const isBillingReady = computed(
  () => storePayload.value.billing_ready !== false
);

const productFeatures = (key, item) => {
  const featuresByKey = {
    additional_user: [
      'Novo acesso para atendimento',
      'Permissões e equipes configuráveis',
      'Cobrança recorrente mensal',
    ],
    additional_inbox: [
      'Canal oficial ou atendimento extra',
      'Organização por caixa de entrada',
      'Cobrança recorrente mensal',
    ],
    consulting: [
      'Revisão de fluxos e integrações',
      'Acompanhamento operacional',
      'Pacote mensal especializado',
    ],
  };

  return (
    featuresByKey[key] || [
      item.description,
      item.unit_label,
      'Cobrança recorrente mensal',
    ]
  ).filter(Boolean);
};

const catalogItems = computed(() =>
  Object.entries(catalog.value).map(([key, item]) => ({
    key,
    featured: false,
    features: productFeatures(key, item),
    ...item,
  }))
);

const selectedItems = computed(() =>
  catalogItems.value
    .map(item => ({
      ...item,
      quantity: Number(quantities[item.key] || 0),
      total: Number(item.unit_price || 0) * Number(quantities[item.key] || 0),
    }))
    .filter(item => item.quantity > 0)
);

const monthlyTotal = computed(() =>
  selectedItems.value.reduce((sum, item) => sum + item.total, 0)
);

const currentSubscriptionValue = computed(() =>
  Number(subscription.value.value || 0)
);
const estimatedSubscriptionValue = computed(
  () => currentSubscriptionValue.value + monthlyTotal.value
);

const formatMoney = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));
const creditUnitsFor = product => {
  const value = creditUnits[product.id];
  return Math.max(
    0,
    Number(value === undefined ? product.minimum_units || 1 : value || 0)
  );
};
const creditTotalFor = product =>
  creditUnitsFor(product) * Number(product.unit_price || 0);
const creditAmountFor = product =>
  creditUnitsFor(product) * Number(product.credits_per_unit || 0);
const minimumCreditsFor = product =>
  Number(product.minimum_units || 1) * Number(product.credits_per_unit || 0);
const isBelowCreditMinimum = product =>
  creditUnitsFor(product) < Number(product.minimum_units || 1);

const creditProductPresentations = {
  lemit_cpf: {
    description:
      'Encontre e valide dados de um CPF, incluindo informações cadastrais, telefones, WhatsApp e endereços.',
    exampleTitle: 'Exemplo de consulta de Higienização',
    exampleDescription:
      'Você informa um CPF válido e recebe os dados localizados de forma organizada.',
    type: 'hygiene',
  },
  nova_era_cpf: {
    description:
      'Localize dados cadastrais, perfil e celulares com WhatsApp a partir de um CPF.',
    exampleTitle: 'Exemplo de consulta Nova Era',
    exampleDescription:
      'Você informa um CPF válido e recebe os dados cadastrais e contatos localizados.',
    type: 'hygiene',
  },
  captain_ai: {
    description:
      'Saldo pré-pago para o Captain responder clientes com IA usando a estrutura global da OpenAI.',
    exampleTitle: 'Exemplo de resposta da IA',
    exampleDescription:
      'O Captain interpreta a mensagem, consulta o contexto da conversa e prepara uma resposta natural.',
    type: 'ai',
  },
};

const presentationFor = product => creditProductPresentations[product.code];
const productDescription = product =>
  presentationFor(product)?.description || product.description;
const openCreditExample = product => {
  selectedCreditExample.value = presentationFor(product);
  creditExampleDialogRef.value?.open();
};

const loadStore = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaStoreAPI.get();
    storePayload.value = data;
    (data.credit_products || []).forEach(product => {
      if (creditUnits[product.id] === undefined) {
        creditUnits[product.id] = Number(product.minimum_units || 1);
      }
    });
  } catch {
    useAlert(copy.messages.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const addItem = key => {
  quantities[key] = Number(quantities[key] || 0) + 1;
};

const removeItem = key => {
  quantities[key] = Math.max(0, Number(quantities[key] || 0) - 1);
};

const openConfirmDialog = () => {
  if (!isBillingReady.value) {
    useAlert(billingWarning.value || copy.messages.billingNotReady);
    return;
  }
  if (hasOpenBankSlips.value) {
    useAlert(copy.messages.blocked);
    return;
  }
  if (hasPendingPurchase.value) {
    useAlert(copy.messages.pendingPurchase);
    return;
  }
  confirmDialogRef.value?.open();
};

const confirmPurchase = async () => {
  isSaving.value = true;
  try {
    const items = selectedItems.value.map(item => ({
      key: item.key,
      quantity: item.quantity,
    }));
    const { data } = await TalkviaStoreAPI.purchase(items);
    storePayload.value = data;
    Object.keys(quantities).forEach(key => {
      quantities[key] = 0;
    });
    confirmDialogRef.value?.close();
    useAlert(copy.messages.success);
  } catch (error) {
    useAlert(error.response?.data?.error || copy.messages.failed);
  } finally {
    isSaving.value = false;
  }
};

const buyCredits = async product => {
  buyingCreditProductId.value = product.id;
  try {
    const units = Number(creditUnits[product.id] || product.minimum_units || 1);
    const { data } = await TalkviaStoreAPI.purchaseCredits(product.id, units);
    window.open(data.bank_slip_url || data.invoice_url, '_blank');
    useAlert(
      'Boleto gerado. Os créditos serão liberados após a confirmação do pagamento.',
      { variant: 'positive' }
    );
    await loadStore();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Não foi possível gerar o boleto.');
  } finally {
    buyingCreditProductId.value = null;
  }
};

onMounted(loadStore);
</script>

<template>
  <main class="h-full min-h-0 w-full overflow-y-auto">
    <div class="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8 pb-24">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p
            class="mb-1 text-xs font-semibold uppercase tracking-wide text-n-brand"
          >
            {{ copy.labels.brand }}
          </p>
          <h1 class="mb-2 text-3xl font-semibold text-n-slate-12">
            {{ copy.title }}
          </h1>
          <p class="mb-0 max-w-3xl text-base text-n-slate-11">
            {{ copy.subtitle }}
          </p>
        </div>
        <button class="btn btn-secondary" @click="loadStore">
          {{ copy.actions.reload }}
        </button>
      </header>

      <div
        v-if="isLoading"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-5"
      >
        {{ copy.loading }}
      </div>

      <section v-else class="grid gap-6 xl:grid-cols-[1fr_390px]">
        <div
          v-if="referralDiscount > 0"
          class="xl:col-span-2 flex items-center gap-3 rounded-xl border border-n-teal-6 bg-n-teal-2 p-4 text-n-teal-12"
        >
          <i class="i-lucide-badge-percent size-6 shrink-0" />
          <div>
            <strong>Benefício de indicação aplicado</strong>
            <p class="mb-0 text-sm">
              Seu nível garante {{ referralDiscount }}% de desconto nos preços
              abaixo.
            </p>
          </div>
        </div>
        <section
          v-if="creditProducts.length"
          class="xl:col-span-2 rounded-xl border border-n-weak bg-n-solid-1 p-5"
        >
          <h2 class="mb-1 text-xl font-semibold text-n-slate-12">
            Créditos de consultas
          </h2>
          <p class="mb-5 text-sm text-n-slate-11">
            Compra segura por boleto, com liberação automática após a
            confirmação do pagamento.
          </p>
          <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <article
              v-for="product in creditProducts"
              :key="product.id"
              class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-5"
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <strong class="text-lg text-n-slate-12">{{
                    product.name
                  }}</strong>
                  <p
                    class="mt-1 min-h-[60px] text-sm leading-5 text-n-slate-11"
                  >
                    {{ productDescription(product) }}
                  </p>
                  <div class="mt-2 h-5">
                    <button
                      v-if="presentationFor(product)"
                      type="button"
                      class="inline-flex items-center gap-1.5 text-sm font-semibold text-n-brand hover:underline"
                      @click="openCreditExample(product)"
                    >
                      <i class="i-lucide-eye size-4" />
                      Ver exemplo
                    </button>
                  </div>
                </div>
                <span
                  class="shrink-0 rounded-full bg-n-brand/10 px-3 py-1 text-xs font-semibold text-n-brand"
                >
                  Saldo: {{ product.balance }}
                </span>
              </div>

              <div
                class="mt-4 rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11"
              >
                <strong class="text-n-slate-12">{{
                  formatMoney(product.unit_price)
                }}</strong>
                <span
                  v-if="product.original_unit_price > product.unit_price"
                  class="ml-2 text-xs text-n-slate-10 line-through"
                  >{{ formatMoney(product.original_unit_price) }}</span
                >
                por pacote de {{ product.credits_per_unit }} crédito<span
                  v-if="product.credits_per_unit !== 1"
                  >s</span
                >
              </div>

              <Input
                v-model="creditUnits[product.id]"
                type="number"
                label="Quantidade de pacotes"
                :min="String(product.minimum_units)"
                :placeholder="String(product.minimum_units)"
                class="mt-4"
              />

              <div
                v-if="isBelowCreditMinimum(product)"
                class="mt-3 flex items-start gap-2 rounded-lg bg-n-amber-2 px-3 py-2.5 text-sm text-n-amber-11"
              >
                <i class="i-lucide-info mt-0.5 size-4 shrink-0" />
                <span>
                  Compra mínima de
                  <strong>
                    {{ minimumCreditsFor(product).toLocaleString('pt-BR') }}
                    créditos
                  </strong>
                  ({{ product.minimum_units }} pacote<span
                    v-if="Number(product.minimum_units) !== 1"
                    >s</span
                  >).
                </span>
              </div>

              <dl class="mt-4 grid gap-2 border-t border-n-weak pt-4 text-sm">
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-n-slate-11">Créditos da compra</dt>
                  <dd class="font-semibold text-n-slate-12">
                    {{ creditAmountFor(product).toLocaleString('pt-BR') }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-n-slate-11">Valor estimado</dt>
                  <dd class="text-lg font-semibold text-n-brand">
                    {{ formatMoney(creditTotalFor(product)) }}
                  </dd>
                </div>
              </dl>

              <div class="mt-auto pt-4">
                <small
                  class="mb-2 block min-h-5 text-n-teal-11"
                  :class="{ invisible: !product.special }"
                  >Condição especial aplicada</small
                >
                <Button
                  label="Comprar"
                  icon="i-lucide-shopping-cart"
                  :color="isBelowCreditMinimum(product) ? 'ruby' : 'blue'"
                  class="w-full"
                  :is-loading="buyingCreditProductId === product.id"
                  :disabled="
                    buyingCreditProductId !== null ||
                    isBelowCreditMinimum(product)
                  "
                  @click="buyCredits(product)"
                />
              </div>
            </article>
          </div>
        </section>
        <div class="grid gap-6">
          <section class="grid gap-5">
            <div class="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 class="mb-1 text-xl font-semibold text-n-slate-12">
                  {{ copy.sections.catalog }}
                </h2>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ copy.sections.catalogDescription }}
                </p>
              </div>
            </div>
            <div class="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
              <article
                v-for="item in catalogItems"
                :key="item.key"
                class="store-plan"
                :class="{ 'store-plan--featured': item.featured }"
              >
                <div class="store-plan__top">
                  <span v-if="item.featured" class="store-plan__badge">
                    {{ copy.labels.recommended }}
                  </span>
                  <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
                    {{ item.name }}
                  </h3>
                  <p class="mb-0 min-h-12 text-sm leading-6 text-n-slate-11">
                    {{ item.description }}
                  </p>
                  <div class="mt-6">
                    <p
                      class="mb-0 flex items-end gap-1 text-3xl font-semibold text-n-slate-12"
                    >
                      {{ formatMoney(item.unit_price) }}
                      <span class="pb-1 text-sm font-normal text-n-slate-10">
                        {{ copy.labels.byMonth }}
                      </span>
                    </p>
                    <p
                      v-if="item.original_unit_price > item.unit_price"
                      class="mb-0 mt-1 text-sm text-n-slate-10 line-through"
                    >
                      {{ formatMoney(item.original_unit_price) }}
                    </p>
                    <p class="mb-0 mt-1 text-sm text-n-slate-10">
                      {{ item.unit_label }}
                    </p>
                  </div>
                </div>

                <ul class="store-plan__features">
                  <li v-for="feature in item.features" :key="feature">
                    <span class="store-plan__check">
                      <i class="i-lucide-check size-4" />
                    </span>
                    <span>{{ feature }}</span>
                  </li>
                </ul>

                <div class="mt-6 grid gap-3">
                  <div class="store-plan__quantity">
                    <button
                      class="stepper"
                      :aria-label="copy.actions.remove"
                      @click="removeItem(item.key)"
                    >
                      <i class="i-lucide-minus size-4" />
                    </button>
                    <span
                      class="min-w-8 text-center font-semibold text-n-slate-12"
                    >
                      {{ quantities[item.key] || 0 }}
                    </span>
                    <button
                      class="stepper"
                      :aria-label="copy.actions.add"
                      @click="addItem(item.key)"
                    >
                      <i class="i-lucide-plus size-4" />
                    </button>
                  </div>
                  <button
                    class="btn btn-primary w-full"
                    @click="addItem(item.key)"
                  >
                    <i class="i-lucide-shopping-cart size-4" />
                    {{ copy.actions.add }}
                  </button>
                </div>
              </article>
            </div>
          </section>
        </div>

        <aside class="grid content-start gap-6">
          <section
            v-if="billingWarning"
            class="rounded-lg border border-n-amber-6 bg-n-amber-2 p-4 text-n-amber-12"
          >
            <h2 class="mb-2 text-base font-semibold">
              {{ copy.billing.notReady }}
            </h2>
            <p class="mb-0 text-sm">{{ billingWarning }}</p>
          </section>

          <section
            v-if="hasOpenBankSlips || hasPendingPurchase"
            class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-4 text-n-ruby-12"
          >
            <h2 class="mb-2 text-base font-semibold">
              {{ copy.fields.openBills }}
            </h2>
            <p class="mb-3 text-sm">
              {{
                hasPendingPurchase
                  ? copy.messages.pendingPurchase
                  : copy.messages.blocked
              }}
            </p>
            <div v-if="hasOpenBankSlips" class="grid gap-2">
              <a
                v-for="payment in openBankSlips"
                :key="payment.id"
                :href="payment.invoiceUrl || payment.paymentLink"
                target="_blank"
                rel="noopener noreferrer"
                class="rounded-md bg-n-ruby-3 px-3 py-2 text-sm font-medium text-n-ruby-12"
              >
                {{ payment.description || payment.id }}
                {{ copy.labels.dash }}
                {{ formatMoney(payment.value) }}
              </a>
            </div>
          </section>

          <section class="rounded-lg border border-n-weak bg-n-solid-1 p-5">
            <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
              {{ copy.sections.cart }}
            </h2>
            <div v-if="!selectedItems.length" class="text-sm text-n-slate-11">
              {{ copy.empty.cart }}
            </div>
            <div v-else class="grid gap-3">
              <article
                v-for="item in selectedItems"
                :key="item.key"
                class="rounded-lg bg-n-alpha-2 p-3"
              >
                <div class="flex justify-between gap-3">
                  <div>
                    <p class="mb-1 font-medium text-n-slate-12">
                      {{ item.quantity }}{{ copy.labels.times }} {{ item.name }}
                    </p>
                    <p class="mb-0 text-sm text-n-slate-10">
                      {{ formatMoney(item.unit_price) }}
                      {{ copy.labels.byMonth }}
                    </p>
                  </div>
                  <strong class="text-n-slate-12">
                    {{ formatMoney(item.total) }}
                  </strong>
                </div>
              </article>
            </div>

            <div class="mt-5 grid gap-3 border-t border-n-weak pt-4">
              <div class="flex justify-between text-sm text-n-slate-11">
                <span>{{ copy.fields.currentSubscription }}</span>
                <strong class="text-n-slate-12">
                  {{ formatMoney(currentSubscriptionValue) }}
                </strong>
              </div>
              <div class="flex justify-between text-sm text-n-slate-11">
                <span>{{ copy.fields.monthlyIncrease }}</span>
                <strong class="text-n-slate-12">
                  {{ formatMoney(monthlyTotal) }}
                </strong>
              </div>
              <div class="flex justify-between text-base text-n-slate-12">
                <span>{{ copy.fields.newEstimatedValue }}</span>
                <strong>{{ formatMoney(estimatedSubscriptionValue) }}</strong>
              </div>
            </div>

            <button
              class="btn btn-primary mt-5 w-full"
              :disabled="
                !selectedItems.length ||
                hasOpenBankSlips ||
                hasPendingPurchase ||
                !isBillingReady
              "
              @click="openConfirmDialog"
            >
              {{ copy.actions.confirm }}
            </button>
          </section>

          <section class="rounded-lg border border-n-weak bg-n-solid-1 p-5">
            <h2 class="mb-3 text-lg font-semibold text-n-slate-12">
              {{ copy.sections.guidance }}
            </h2>
            <ul class="m-0 grid list-none gap-3 p-0">
              <li
                v-for="item in guidance"
                :key="item"
                class="flex items-start gap-2 text-sm leading-5 text-n-slate-11"
              >
                <i
                  class="i-lucide-circle-check mt-0.5 size-4 shrink-0 text-n-teal-10"
                />
                <span>{{ item }}</span>
              </li>
            </ul>
          </section>
        </aside>
      </section>

      <Dialog ref="confirmDialogRef" width="2xl" @confirm="confirmPurchase">
        <template #default>
          <div class="grid gap-4">
            <h2 class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ copy.actions.confirm }}
            </h2>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ copy.sections.cart }}{{ copy.labels.colon }}
              {{ formatMoney(monthlyTotal) }} {{ copy.labels.recurring
              }}{{ copy.labels.periodMark }}
            </p>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ copy.fields.newEstimatedValue }}{{ copy.labels.colon }}
              {{ formatMoney(estimatedSubscriptionValue)
              }}{{ copy.labels.periodMark }}
            </p>
          </div>
        </template>
        <template #footer>
          <div class="flex w-full justify-end gap-3">
            <button
              class="btn btn-ghost"
              type="button"
              @click="confirmDialogRef.close()"
            >
              {{ copy.actions.cancel }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isSaving"
              @click="confirmPurchase"
            >
              {{ copy.actions.confirm }}
            </button>
          </div>
        </template>
      </Dialog>

      <Dialog
        ref="creditExampleDialogRef"
        width="2xl"
        :title="selectedCreditExample?.exampleTitle"
        :description="selectedCreditExample?.exampleDescription"
        :show-cancel-button="false"
        confirm-button-label="Entendi"
        @confirm="creditExampleDialogRef.close()"
      >
        <div
          v-if="selectedCreditExample?.type === 'hygiene'"
          class="grid gap-4"
        >
          <div class="rounded-lg border border-n-weak bg-n-solid-2 p-4">
            <p class="mb-3 text-xs font-semibold uppercase text-n-slate-10">
              Resultado ilustrativo
            </p>
            <div class="grid gap-3 sm:grid-cols-2">
              <div class="credit-example-field">
                <span>Nome</span><strong>Cliente localizado</strong>
              </div>
              <div class="credit-example-field">
                <span>Situação do CPF</span><strong>Regular</strong>
              </div>
              <div class="credit-example-field">
                <span>Celular</span><strong>(11) 9****-1234</strong>
                <small class="text-n-teal-11">WhatsApp confirmado</small>
              </div>
              <div class="credit-example-field">
                <span>Localização</span><strong>São Paulo — SP</strong>
              </div>
            </div>
          </div>
          <p class="mb-0 text-xs leading-5 text-n-slate-10">
            Os campos encontrados variam conforme a disponibilidade das fontes
            consultadas. Os dados deste exemplo são fictícios.
          </p>
        </div>

        <div
          v-else-if="selectedCreditExample?.type === 'ai'"
          class="grid gap-3"
        >
          <div
            class="ml-auto max-w-[85%] rounded-xl bg-n-brand px-4 py-3 text-sm text-white"
          >
            Olá! Gostaria de saber quais documentos preciso enviar.
          </div>
          <div
            class="max-w-[90%] rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 text-sm leading-6 text-n-slate-12"
          >
            <div
              class="mb-2 flex items-center gap-2 font-semibold text-n-brand"
            >
              <i class="i-lucide-sparkles size-4" />Captain IA
            </div>
            Claro! Vou orientar você. Para começar, envie um documento com foto
            e o comprovante solicitado no atendimento. Se preferir, também posso
            explicar cada etapa do processo.
          </div>
          <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-10">
            Exemplo ilustrativo. O consumo de créditos varia conforme o modelo
            de IA utilizado e o processamento necessário para cada resposta.
          </p>
        </div>
      </Dialog>
    </div>
  </main>
</template>

<style scoped>
.btn {
  @apply inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60;
}

.btn-primary {
  @apply bg-n-brand text-white hover:opacity-90;
}

.btn-secondary {
  @apply border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2;
}

.btn-ghost {
  @apply text-n-slate-11 hover:text-n-slate-12;
}

.stepper {
  @apply flex size-10 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2;
}

.store-plan {
  @apply relative flex min-h-[440px] flex-col justify-between overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-n-brand/40 hover:shadow-md;
}

.store-plan::before {
  @apply absolute inset-x-0 top-0 h-1 bg-n-alpha-3 content-[''];
}

.store-plan--featured {
  @apply border-n-brand bg-n-solid-2 shadow-[0_18px_48px_rgba(37,99,235,0.16)];
}

.store-plan--featured::before {
  @apply bg-n-brand;
}

.store-plan__top {
  @apply relative;
}

.store-plan__badge {
  @apply mb-4 inline-flex rounded-full bg-n-brand px-3 py-1 text-xs font-semibold text-white;
}

.store-plan__features {
  @apply mt-6 grid list-none gap-3 border-t border-n-weak p-0 pt-5;
}

.store-plan__features li {
  @apply flex items-start gap-3 text-sm leading-5 text-n-slate-11;
}

.store-plan__check {
  @apply mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-n-brand/10 text-n-brand;
}

.store-plan__quantity {
  @apply flex items-center justify-center gap-3 rounded-lg bg-n-alpha-2 p-2;
}

.credit-example-field {
  @apply flex min-h-20 flex-col rounded-lg bg-n-alpha-2 p-3 text-sm;
}

.credit-example-field span {
  @apply mb-1 text-xs text-n-slate-10;
}

.credit-example-field strong {
  @apply text-n-slate-12;
}
</style>
